/**
 * useRouteHead — 라우트 meta 기반 문서 제목·설명을 한 곳에서 주입한다.
 *
 * 규칙:
 *  - `meta.title` 있음 → `{title} · {brand.name}` (예: "대학 검색 · 대학 합격의 길")
 *  - 없음(홈) → `{brand.name} · {brand.tagline}` — 브랜드 검색 결과에 홈이 브랜드명으로 노출되도록 브랜드 우선.
 *  - `meta.description` 있으면 `<meta name="description">` 도 함께(없으면 index.html 정적 기본값 유지).
 *  - 색인 대상이 아닌 경로(`config/seoRoutes.ts` `isIndexablePath` 거짓)는 `robots: noindex` — 마이페이지·리포트
 *    상세·존재하지 않는 URL 이 SPA 셸(200 응답)로 열려도 검색 결과에 남지 않게 한다(soft 404 차단). 구글은 JS 로
 *    추가된 noindex 를 따른다. 반대로 색인 대상에는 robots 를 내지 않는다(기본값 index,follow).
 *  - 색인 패턴에 맞아도 페이지가 데이터 없음을 알리면(`usePageNotFound` → `stores/pageStatus`) noindex —
 *    `/notice/999` 같은 URL 의 "찾을 수 없음" 화면이 정식 문서로 색인되지 않게 한다.
 *
 * ⚠️ 페이지마다 `useHead({ title })` 를 흩뿌리지 않는다 — 누락된 화면이 전부 같은 제목으로 떨어져
 * GA4 "페이지 제목" 보고서에서 홈과 합산되고(2026-09 5.5천 조회가 한 행으로 뭉친 사고), 검색엔진은
 * 홈·자료 페이지를 중복 문서로 보고 대표 URL 을 임의로 고른다. 제목은 라우트 정의(`router/index.ts`)가 SSoT.
 *
 * 데이터 기반 head 를 자체 발행하는 페이지(가이드 `useSeoHead`·약관·이벤트)는 컴포넌트 `useHead` 가
 * 이 엔트리보다 나중에 등록되므로 그쪽 제목이 우선한다(unhead: 마지막 등록 승). 해당 라우트는 meta.title 을 두지 않는다.
 *
 * App.vue 에서 한 번만 호출한다(`useCanonicalHead` 와 같은 자리).
 */
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { brand } from '#/config/brand';
import { isIndexablePath } from '#/config/seoRoutes';
import { usePageStatusStore } from '#/stores/pageStatus';

/** meta.title 이 없는 라우트(홈)의 문서 제목 — 브랜드명이 앞. */
export const DEFAULT_DOCUMENT_TITLE = `${brand.name} · ${brand.tagline}`;

/** 라우트 meta.title 로 문서 제목을 조립한다(브랜드 접미). */
export function buildDocumentTitle(title: string | undefined): string {
  return title ? `${title} · ${brand.name}` : DEFAULT_DOCUMENT_TITLE;
}

/** 현재 라우트의 meta.title / meta.description 을 head 에 주입한다. */
export function useRouteHead(): void {
  const route = useRoute();
  const pageStatus = usePageStatusStore();

  const title = computed(() => buildDocumentTitle(route.meta.title));
  const description = computed(() => route.meta.description ?? null);

  const noindex = computed(() => !isIndexablePath(route.path) || pageStatus.isNotFound(route.path));
  useHead({
    title,
    meta: () => [
      ...(description.value ? [{ name: 'description', content: description.value }] : []),
      ...(noindex.value ? [{ name: 'robots', content: 'noindex' }] : []),
    ],
  });
}
